import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { createAdminRagItem, updateAdminRagItem } from '@/lib/api/admin';

function emptyForm() {
  return {
    sourceId: '',
    section: '',
    question: '',
    answer: '',
    aliases: [],
  };
}

function fromItem(item) {
  if (!item) return emptyForm();
  return {
    sourceId: String(item?.metadata?.sourceId || '').trim(),
    section: String(item?.section || ''),
    question: String(item?.question || ''),
    answer: String(item?.answer || ''),
    aliases: Array.isArray(item?.aliases) ? [...item.aliases] : [],
  };
}

function RagItemEditorDialog({ open, item, onOpenChange, onSaved }) {
  const isEditing = Boolean(item?.id);
  const [form, setForm] = useState(() => fromItem(item));
  const [aliasDraft, setAliasDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(fromItem(item));
      setAliasDraft('');
      setError(null);
    }
  }, [open, item]);

  const isValid = useMemo(() => {
    const answer = form.answer.trim();
    if (!answer) return false;
    const hasQuestionOrAlias =
      form.question.trim().length > 0 || form.aliases.some((a) => String(a || '').trim().length > 0);
    return hasQuestionOrAlias;
  }, [form]);

  const setField = (name) => (event) => {
    const value = event?.target?.value ?? event;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const addAliases = (raw) => {
    if (!raw) return;
    const parts = String(raw)
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    setForm((current) => {
      const set = new Set(current.aliases.map((a) => a.trim()));
      const next = [...current.aliases];
      for (const p of parts) {
        if (!set.has(p)) {
          set.add(p);
          next.push(p);
        }
      }
      return { ...current, aliases: next };
    });
    setAliasDraft('');
  };

  const removeAlias = (idx) => {
    setForm((current) => ({
      ...current,
      aliases: current.aliases.filter((_, i) => i !== idx),
    }));
  };

  const handleAliasKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addAliases(aliasDraft);
    } else if (event.key === 'Backspace' && !aliasDraft && form.aliases.length > 0) {
      // Quick undo of the last alias chip.
      setForm((current) => ({
        ...current,
        aliases: current.aliases.slice(0, -1),
      }));
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!isValid || isSaving) return;
    setIsSaving(true);
    setError(null);
    const trimmedAliases = form.aliases.map((a) => a.trim()).filter(Boolean);
    const payload = {
      section: form.section.trim(),
      question: form.question.trim(),
      answer: form.answer.trim(),
      aliases: trimmedAliases,
      sourceId: form.sourceId.trim(),
    };
    try {
      if (isEditing) {
        await updateAdminRagItem({ id: item.id, ...payload });
        toast.success('Item updated');
      } else {
        await createAdminRagItem(payload);
        toast.success('Item created');
      }
      onSaved?.();
    } catch (err) {
      if (err?.status === 409) {
        setError(`Source ID is already used by item ${err?.payload?.conflictId || 'another row'}.`);
      } else {
        setError(err?.message || 'Save failed');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl flex max-h-[90vh] flex-col overflow-hidden p-0">
        <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit item' : 'New item'}</DialogTitle>
          <DialogDescription>
            Fields mirror the canonical FAQ format. Either a question or at least one alias is required, plus an answer.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <Field label="Source ID" hint="Matches `### <id>` in Markdown imports and the ID column in Excel. Leave blank for a UI-only item.">
            <Input
              value={form.sourceId}
              onChange={setField('sourceId')}
              placeholder="e.g. over-1"
              className="font-mono text-sm"
            />
          </Field>

          <Field label="Section">
            <Input
              value={form.section}
              onChange={setField('section')}
              placeholder="e.g. Clinic overview"
            />
          </Field>

          <Field label="Question">
            <Input
              value={form.question}
              onChange={setField('question')}
              placeholder="Primary question phrasing"
            />
          </Field>

          <Field label="Aliases" hint="Alternate phrasings. Press Enter to add, or paste pipe-separated values.">
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-background px-2 py-2">
              {form.aliases.map((alias, idx) => (
                <Badge key={`${alias}-${idx}`} variant="secondary" className="gap-1 pl-2 pr-1">
                  {alias}
                  <button
                    type="button"
                    className="rounded p-0.5 opacity-60 hover:opacity-100"
                    onClick={() => removeAlias(idx)}
                    aria-label={`Remove alias ${alias}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <input
                type="text"
                value={aliasDraft}
                onChange={(event) => setAliasDraft(event.target.value)}
                onKeyDown={handleAliasKeyDown}
                onBlur={() => addAliases(aliasDraft)}
                placeholder={form.aliases.length === 0 ? 'Tell me about your clinic.' : ''}
                className="min-w-[12ch] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          </Field>

          <Field label="Answer">
            <textarea
              value={form.answer}
              onChange={setField('answer')}
              rows={10}
              className={cn(
                'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              placeholder="Canonical answer text. Markdown allowed."
            />
          </Field>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSaving}>
              {isSaving && <Spinner className="mr-2 h-4 w-4" />}
              {isEditing ? 'Save changes' : 'Create item'}
            </Button>
          </DialogFooter>
        </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export default RagItemEditorDialog;
